import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ArrowsLeftRight, ClockClockwise } from '@phosphor-icons/react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Header fields we compare side-by-side. Each entry is [key, label, formatter].
const HEADER_FIELDS = [
  ['status', 'Status'],
  ['customer_name', 'Customer'],
  ['date', 'Date'],
  ['valid_until', 'Valid Until'],
  ['payment_terms', 'Payment Terms'],
  ['vat_category', 'VAT Category'],
  ['reference', 'Reference'],
  ['po_reference', 'PO Reference'],
  ['project_reference', 'Project Ref'],
  ['project_location', 'Project Location'],
  ['salutation', 'Salutation'],
  ['scope_of_works', 'Scope of Works'],
  ['exclusions', 'Exclusions'],
  ['payment_terms_detail', 'Payment Terms Detail'],
  ['terms_conditions', 'Terms & Conditions'],
  ['notes', 'Notes'],
  ['overall_discount', 'Overall Discount'],
  ['overall_discount_type', 'Discount Type'],
];

const TOTAL_FIELDS = [
  ['subtotal', 'Subtotal'],
  ['discount_total', 'Discount'],
  ['taxable_amount', 'Taxable Amount'],
  ['tax_total', 'VAT / Tax'],
  ['retention_amount', 'Retention'],
  ['advance_payment', 'Advance Received'],
  ['previous_payments', 'Previous Payments'],
  ['final_payable', 'Current Payable'],
  ['total', 'Grand Total'],
];

const normalize = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return Number(v).toString();
  return String(v).trim();
};

const isDifferent = (a, b) => normalize(a) !== normalize(b);

const formatVal = (v) => {
  if (v === null || v === undefined || v === '') return <span className="text-muted-foreground/60 italic">empty</span>;
  if (typeof v === 'number') return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return String(v);
};

// Build a side-by-side diff between two arrays of line items.
// Matching strategy: each A line is paired with the first unmatched B line that
// shares the same lowercased description. Unmatched A → REMOVED, unmatched B
// → ADDED. This handles re-ordering, insertions, and partial overlaps without
// forcing unrelated lines onto the same row.
const diffLines = (linesA = [], linesB = []) => {
  const keyOf = (l) => (l?.description || '').toLowerCase().trim();
  const FIELDS = ['description', 'quantity', 'unit_price', 'tax_rate', 'discount', 'unit', 'specification', 'line_total'];

  const usedB = new Set();
  const rows = [];

  // Pass 1: walk A, pair each with first unmatched B sharing the same description
  linesA.forEach((la) => {
    const k = keyOf(la);
    let pairedIdx = -1;
    for (let j = 0; j < linesB.length; j++) {
      if (usedB.has(j)) continue;
      if (keyOf(linesB[j]) === k) {
        pairedIdx = j;
        break;
      }
    }
    if (pairedIdx !== -1) {
      const lb = linesB[pairedIdx];
      usedB.add(pairedIdx);
      const changed = FIELDS.some(f => isDifferent(la[f], lb[f]));
      rows.push({ kind: changed ? 'modified' : 'unchanged', a: la, b: lb });
    } else {
      rows.push({ kind: 'removed', a: la, b: null });
    }
  });

  // Pass 2: any B lines that weren't paired with an A line → added
  linesB.forEach((lb, idx) => {
    if (!usedB.has(idx)) {
      rows.push({ kind: 'added', a: null, b: lb });
    }
  });

  return rows;
};

const LineCell = ({ value, otherValue, kind }) => {
  // kind: 'added' | 'removed' | 'changed' | 'same'
  const cls = {
    added: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 font-semibold',
    removed: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 line-through',
    changed: 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 font-semibold',
    same: 'text-foreground',
  };
  // When called with an `otherValue` and no explicit kind, infer it: if values
  // differ, treat as 'changed' (amber). Callers that already know whether the
  // cell is on the FROM side (red strike) or TO side (green add) should pass
  // kind explicitly.
  const isChanged = otherValue !== undefined && isDifferent(value, otherValue);
  const c = kind || (isChanged ? 'changed' : 'same');
  return <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${cls[c]}`}>{formatVal(value)}</span>;
};

// Helper to pick the right kind for a cell inside a "modified" row.
const cellKind = (side, valA, valB) => {
  if (!isDifferent(valA, valB)) return 'same';
  return side === 'from' ? 'removed' : 'added';
};

export default function RevisionDiffViewer({ open, onClose, quotationId, documentNumber }) {
  const [revisions, setRevisions] = useState([]); // metadata list
  const [fullDocs, setFullDocs] = useState({}); // {id: fullDoc}
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [loading, setLoading] = useState(false);
  // When true, show ALL header fields (even unchanged). Default OFF so the
  // line items section gets attention when only lines/totals were modified.
  const [showAllHeaders, setShowAllHeaders] = useState(false);

  // Load revision metadata list when opened
  useEffect(() => {
    if (!open || !quotationId) return;
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/workflow/revisions/quotations/${quotationId}`);
        if (cancel) return;
        const list = (res.data?.revisions || []).slice().sort((a, b) => {
          const ra = parseInt((a.revision || 'R0').replace('R', ''), 10) || 0;
          const rb = parseInt((b.revision || 'R0').replace('R', ''), 10) || 0;
          return ra - rb;
        });
        setRevisions(list);
        if (list.length >= 2) {
          setFromId(list[list.length - 2].id);
          setToId(list[list.length - 1].id);
        } else if (list.length === 1) {
          setFromId(list[0].id);
          setToId(list[0].id);
        }
      } catch (e) {
        toast.error('Failed to load revision history');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, quotationId]);

  // Fetch full docs for fromId / toId as they change (cache in fullDocs)
  useEffect(() => {
    if (!open) return;
    const idsToFetch = [fromId, toId].filter(id => id && !fullDocs[id]);
    if (idsToFetch.length === 0) return;
    let cancel = false;
    (async () => {
      try {
        const results = await Promise.all(
          idsToFetch.map(id => axios.get(`${API_URL}/quotations/${id}`).then(r => [id, r.data]))
        );
        if (cancel) return;
        setFullDocs(prev => {
          const next = { ...prev };
          results.forEach(([id, doc]) => { next[id] = doc; });
          return next;
        });
      } catch (e) {
        toast.error('Failed to load full revision data');
      }
    })();
    return () => { cancel = true; };
  }, [open, fromId, toId, fullDocs]);

  const docA = fullDocs[fromId];
  const docB = fullDocs[toId];

  const headerDiffRows = useMemo(() => {
    if (!docA || !docB) return [];
    const all = HEADER_FIELDS
      .map(([key, label]) => ({ key, label, a: docA[key], b: docB[key], changed: isDifferent(docA[key], docB[key]) }))
      .filter(r => r.changed || normalize(r.a) || normalize(r.b));
    // Default: show only changed rows. Toggle "Show all" to see unchanged ones too.
    return showAllHeaders ? all : all.filter(r => r.changed);
  }, [docA, docB, showAllHeaders]);

  const headerChangedCount = useMemo(() => {
    if (!docA || !docB) return 0;
    return HEADER_FIELDS.filter(([key]) => isDifferent(docA[key], docB[key])).length;
  }, [docA, docB]);

  const lineDiffRows = useMemo(() => {
    if (!docA || !docB) return [];
    return diffLines(docA.lines || [], docB.lines || []);
  }, [docA, docB]);

  const totalsDiffRows = useMemo(() => {
    if (!docA || !docB) return [];
    return TOTAL_FIELDS
      .map(([key, label]) => ({
        key, label,
        a: docA[key], b: docB[key],
        changed: isDifferent(docA[key], docB[key]),
      }))
      // Only display totals rows where at least one side has a meaningful value.
      // Most quotes don't have retention/advance/previous filled in.
      .filter(r => r.changed || (r.a !== null && r.a !== undefined && r.a !== 0) || (r.b !== null && r.b !== undefined && r.b !== 0));
  }, [docA, docB]);

  const changeCounts = useMemo(() => {
    const added = lineDiffRows.filter(r => r.kind === 'added').length;
    const removed = lineDiffRows.filter(r => r.kind === 'removed').length;
    const modified = lineDiffRows.filter(r => r.kind === 'modified').length;
    const totalsChanges = totalsDiffRows.filter(r => r.changed).length;
    return { added, removed, modified, headerChanges: headerChangedCount, totalsChanges };
  }, [headerChangedCount, lineDiffRows, totalsDiffRows]);

  const renderRevisionLabel = (r) => {
    const num = r.revision || 'R0';
    const dateStr = r.last_edited_at
      ? new Date(r.last_edited_at).toLocaleDateString()
      : (r.created_at ? new Date(r.created_at).toLocaleDateString() : '');
    return `${documentNumber}${num !== 'R0' ? ` (${num})` : ''} · ${dateStr}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="revision-diff-viewer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClockClockwise size={20} weight="duotone" />
            Revision Comparison
            <span className="font-mono text-sm text-muted-foreground">— {documentNumber}</span>
          </DialogTitle>
          <DialogDescription>
            Side-by-side comparison of any two revisions of this quotation.
            <span className="text-emerald-600 ml-2">Green = added</span>,
            <span className="text-red-600 ml-2">red = removed</span>,
            <span className="text-amber-600 ml-2">amber = modified</span>.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-muted-foreground">Loading revision history…</div>
        ) : revisions.length < 2 ? (
          <div className="py-12 text-center text-muted-foreground">
            <ClockClockwise size={48} weight="duotone" className="mx-auto mb-3 opacity-40" />
            <p className="font-medium">No revision history yet</p>
            <p className="text-sm mt-1">This quotation has only one version (R0). Once you edit it after client approval / agreement signed, an R1 will be created and you'll be able to compare versions here.</p>
          </div>
        ) : (
          <>
            {/* Revision pickers */}
            <div className="flex flex-col sm:flex-row items-center gap-3 px-1 py-2 border-b">
              <div className="flex-1 w-full">
                <label className="text-xs font-semibold text-muted-foreground mb-1 block">From revision</label>
                <Select value={fromId} onValueChange={setFromId}>
                  <SelectTrigger data-testid="revision-diff-from"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {revisions.map(r => (
                      <SelectItem key={r.id} value={r.id} data-testid={`revision-diff-from-${r.revision || 'R0'}`}>
                        {renderRevisionLabel(r)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <ArrowsLeftRight size={24} className="text-muted-foreground mt-5 hidden sm:block" />
              <div className="flex-1 w-full">
                <label className="text-xs font-semibold text-muted-foreground mb-1 block">To revision</label>
                <Select value={toId} onValueChange={setToId}>
                  <SelectTrigger data-testid="revision-diff-to"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {revisions.map(r => (
                      <SelectItem key={r.id} value={r.id} data-testid={`revision-diff-to-${r.revision || 'R0'}`}>
                        {renderRevisionLabel(r)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Summary badges */}
            {docA && docB && (
              <div className="flex flex-wrap gap-2 px-1 py-2" data-testid="revision-diff-summary">
                <Badge variant="outline" className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200">
                  {changeCounts.headerChanges} header field{changeCounts.headerChanges === 1 ? '' : 's'} changed
                </Badge>
                <Badge variant="outline" className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200">
                  +{changeCounts.added} line{changeCounts.added === 1 ? '' : 's'} added
                </Badge>
                <Badge variant="outline" className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200">
                  -{changeCounts.removed} line{changeCounts.removed === 1 ? '' : 's'} removed
                </Badge>
                <Badge variant="outline" className="bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-amber-200">
                  {changeCounts.modified} line{changeCounts.modified === 1 ? '' : 's'} modified
                </Badge>
                <Badge variant="outline" className="bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-violet-200">
                  {changeCounts.totalsChanges} total{changeCounts.totalsChanges === 1 ? '' : 's'} changed
                </Badge>
              </div>
            )}

            <ScrollArea className="flex-1 pr-3">
              {!docA || !docB ? (
                <div className="py-8 text-center text-muted-foreground">Loading full revisions…</div>
              ) : (
                <div className="space-y-6 py-2">
                  {/* Line items diff */}
                  <section data-testid="revision-diff-lines">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-2">Line items</h3>
                    {lineDiffRows.length === 0 ? (
                      <div className="text-xs text-muted-foreground italic py-2">No line items</div>
                    ) : (
                      <div className="border rounded-lg overflow-hidden overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-muted/40">
                            <tr>
                              <th className="text-center px-2 py-2 font-semibold uppercase tracking-wide text-muted-foreground w-10">#</th>
                              <th className="text-left px-2 py-2 font-semibold uppercase tracking-wide text-muted-foreground w-24">Change</th>
                              <th className="text-left px-2 py-2 font-semibold uppercase tracking-wide text-muted-foreground">Description</th>
                              <th className="text-left px-2 py-2 font-semibold uppercase tracking-wide text-muted-foreground">Unit</th>
                              <th className="text-right px-2 py-2 font-semibold uppercase tracking-wide text-muted-foreground">Qty</th>
                              <th className="text-right px-2 py-2 font-semibold uppercase tracking-wide text-muted-foreground">Unit Price</th>
                              <th className="text-right px-2 py-2 font-semibold uppercase tracking-wide text-muted-foreground">Tax %</th>
                              <th className="text-right px-2 py-2 font-semibold uppercase tracking-wide text-muted-foreground">Disc</th>
                              <th className="text-right px-2 py-2 font-semibold uppercase tracking-wide text-muted-foreground">Line Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {lineDiffRows.map((row, idx) => {
                              const a = row.a || {}; const b = row.b || {};
                              const tag = row.kind;
                              const tagColor = {
                                added: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
                                removed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
                                modified: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
                                unchanged: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
                              }[tag];
                              return (
                                <React.Fragment key={idx}>
                                  {tag === 'modified' && (
                                    <>
                                      <tr className="bg-red-50/40 dark:bg-red-900/10">
                                        <td className="px-2 py-1.5 text-center text-muted-foreground font-mono" rowSpan={2}>{idx + 1}</td>
                                        <td className="px-2 py-1.5"><Badge className={tagColor + ' text-[10px]'}>FROM</Badge></td>
                                        <td className="px-2 py-1.5"><LineCell value={a.description} kind={cellKind('from', a.description, b.description)} /></td>
                                        <td className="px-2 py-1.5"><LineCell value={a.unit} kind={cellKind('from', a.unit, b.unit)} /></td>
                                        <td className="px-2 py-1.5 text-right font-mono"><LineCell value={a.quantity} kind={cellKind('from', a.quantity, b.quantity)} /></td>
                                        <td className="px-2 py-1.5 text-right font-mono"><LineCell value={a.unit_price} kind={cellKind('from', a.unit_price, b.unit_price)} /></td>
                                        <td className="px-2 py-1.5 text-right font-mono"><LineCell value={a.tax_rate} kind={cellKind('from', a.tax_rate, b.tax_rate)} /></td>
                                        <td className="px-2 py-1.5 text-right font-mono"><LineCell value={a.discount} kind={cellKind('from', a.discount, b.discount)} /></td>
                                        <td className="px-2 py-1.5 text-right font-mono"><LineCell value={a.line_total} kind={cellKind('from', a.line_total, b.line_total)} /></td>
                                      </tr>
                                      <tr className="bg-emerald-50/40 dark:bg-emerald-900/10">
                                        <td className="px-2 py-1.5"><Badge className={tagColor + ' text-[10px]'}>TO</Badge></td>
                                        <td className="px-2 py-1.5"><LineCell value={b.description} kind={cellKind('to', a.description, b.description)} /></td>
                                        <td className="px-2 py-1.5"><LineCell value={b.unit} kind={cellKind('to', a.unit, b.unit)} /></td>
                                        <td className="px-2 py-1.5 text-right font-mono"><LineCell value={b.quantity} kind={cellKind('to', a.quantity, b.quantity)} /></td>
                                        <td className="px-2 py-1.5 text-right font-mono"><LineCell value={b.unit_price} kind={cellKind('to', a.unit_price, b.unit_price)} /></td>
                                        <td className="px-2 py-1.5 text-right font-mono"><LineCell value={b.tax_rate} kind={cellKind('to', a.tax_rate, b.tax_rate)} /></td>
                                        <td className="px-2 py-1.5 text-right font-mono"><LineCell value={b.discount} kind={cellKind('to', a.discount, b.discount)} /></td>
                                        <td className="px-2 py-1.5 text-right font-mono"><LineCell value={b.line_total} kind={cellKind('to', a.line_total, b.line_total)} /></td>
                                      </tr>
                                    </>
                                  )}
                                  {tag === 'added' && (
                                    <tr className="bg-emerald-50/40 dark:bg-emerald-900/10">
                                      <td className="px-2 py-1.5 text-center text-muted-foreground font-mono">{idx + 1}</td>
                                      <td className="px-2 py-1.5"><Badge className={tagColor + ' text-[10px]'}>+ ADDED</Badge></td>
                                      <td className="px-2 py-1.5"><LineCell value={b.description} kind="added" /></td>
                                      <td className="px-2 py-1.5"><LineCell value={b.unit} kind="added" /></td>
                                      <td className="px-2 py-1.5 text-right font-mono"><LineCell value={b.quantity} kind="added" /></td>
                                      <td className="px-2 py-1.5 text-right font-mono"><LineCell value={b.unit_price} kind="added" /></td>
                                      <td className="px-2 py-1.5 text-right font-mono"><LineCell value={b.tax_rate} kind="added" /></td>
                                      <td className="px-2 py-1.5 text-right font-mono"><LineCell value={b.discount} kind="added" /></td>
                                      <td className="px-2 py-1.5 text-right font-mono"><LineCell value={b.line_total} kind="added" /></td>
                                    </tr>
                                  )}
                                  {tag === 'removed' && (
                                    <tr className="bg-red-50/40 dark:bg-red-900/10">
                                      <td className="px-2 py-1.5 text-center text-muted-foreground font-mono">{idx + 1}</td>
                                      <td className="px-2 py-1.5"><Badge className={tagColor + ' text-[10px]'}>− REMOVED</Badge></td>
                                      <td className="px-2 py-1.5"><LineCell value={a.description} kind="removed" /></td>
                                      <td className="px-2 py-1.5"><LineCell value={a.unit} kind="removed" /></td>
                                      <td className="px-2 py-1.5 text-right font-mono"><LineCell value={a.quantity} kind="removed" /></td>
                                      <td className="px-2 py-1.5 text-right font-mono"><LineCell value={a.unit_price} kind="removed" /></td>
                                      <td className="px-2 py-1.5 text-right font-mono"><LineCell value={a.tax_rate} kind="removed" /></td>
                                      <td className="px-2 py-1.5 text-right font-mono"><LineCell value={a.discount} kind="removed" /></td>
                                      <td className="px-2 py-1.5 text-right font-mono"><LineCell value={a.line_total} kind="removed" /></td>
                                    </tr>
                                  )}
                                  {tag === 'unchanged' && (
                                    <tr>
                                      <td className="px-2 py-1.5 text-center text-muted-foreground font-mono">{idx + 1}</td>
                                      <td className="px-2 py-1.5"><Badge variant="outline" className="text-[10px] bg-slate-50 dark:bg-slate-800 text-slate-500">no change</Badge></td>
                                      <td className="px-2 py-1.5">{a.description}</td>
                                      <td className="px-2 py-1.5">{a.unit || <span className="text-muted-foreground/50">—</span>}</td>
                                      <td className="px-2 py-1.5 text-right font-mono">{formatVal(a.quantity)}</td>
                                      <td className="px-2 py-1.5 text-right font-mono">{formatVal(a.unit_price)}</td>
                                      <td className="px-2 py-1.5 text-right font-mono">{formatVal(a.tax_rate)}</td>
                                      <td className="px-2 py-1.5 text-right font-mono">{formatVal(a.discount)}</td>
                                      <td className="px-2 py-1.5 text-right font-mono">{formatVal(a.line_total)}</td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>

                  {/* Totals diff */}
                  <section data-testid="revision-diff-totals">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground mb-2">Totals</h3>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/40">
                          <tr>
                            <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground w-1/3">Item</th>
                            <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground w-1/3">From</th>
                            <th className="text-right px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground w-1/3">To</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {totalsDiffRows.map(row => (
                            <tr key={row.key} className={row.changed ? 'bg-amber-50/50 dark:bg-amber-900/10 font-semibold' : ''}>
                              <td className="px-3 py-2 font-medium text-muted-foreground">{row.label}</td>
                              <td className="px-3 py-2 text-right font-mono"><LineCell value={row.a} kind={row.changed ? 'removed' : 'same'} /></td>
                              <td className="px-3 py-2 text-right font-mono"><LineCell value={row.b} kind={row.changed ? 'added' : 'same'} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  {/* Header diff (placed last because most revisions are line-item-driven; expand via "Show all" to see unchanged context) */}
                  <section data-testid="revision-diff-header">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
                        Header fields
                        <span className="ml-2 text-xs font-normal normal-case text-muted-foreground/70">
                          ({headerChangedCount} changed)
                        </span>
                      </h3>
                      <label className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={showAllHeaders}
                          onChange={(e) => setShowAllHeaders(e.target.checked)}
                          className="h-3.5 w-3.5 rounded border-border accent-amber-500"
                          data-testid="revision-diff-show-all-headers"
                        />
                        Show all header fields
                      </label>
                    </div>
                    {headerDiffRows.length === 0 ? (
                      <div className="text-xs text-muted-foreground italic py-2">
                        {headerChangedCount === 0
                          ? 'No header fields changed between these two revisions.'
                          : 'Toggle "Show all header fields" to view unchanged fields too.'}
                      </div>
                    ) : (
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/40">
                            <tr>
                              <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground w-1/5">Field</th>
                              <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground w-2/5">From</th>
                              <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground w-2/5">To</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {headerDiffRows.map(row => (
                              <tr key={row.key} className={row.changed ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}>
                                <td className="px-3 py-2 font-medium text-muted-foreground">{row.label}</td>
                                <td className="px-3 py-2 whitespace-pre-wrap"><LineCell value={row.a} kind={row.changed ? 'removed' : 'same'} /></td>
                                <td className="px-3 py-2 whitespace-pre-wrap"><LineCell value={row.b} kind={row.changed ? 'added' : 'same'} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                </div>
              )}
            </ScrollArea>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
